import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { PhoneIcon, MicIcon, CalendarIcon, DatabaseIcon, InboxIcon, ArrowRightIcon, CheckCircleIcon, MessageSquareIcon, GlobeIcon, ClockIcon, ZapIcon, SmartphoneIcon, MessageCircleIcon, UserIcon, BotIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
export function AutomationsPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-403">
      <SEOHead title="Marketing Automations & AI Chatbots | Viridia Analytics Phoenix" description="Never miss a lead again. Speed-to-lead automations including Yelp AI conversational chatbots, missed-call text back, and multi-platform AI responders for Phoenix service businesses." canonical="/automations" keywords="marketing automation Phoenix, Yelp AI chatbot, speed to lead, missed call text back, multi-platform AI responder, business automation Phoenix" data-id="element-404" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-405">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-406">
          <div className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-407"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-408">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-409">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-410">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-411">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-412">
                  <span className="w-2 h-2 rounded-full bg-viridia mr-2" data-id="element-413"></span>
                  SPEED TO LEAD IS EVERYTHING
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-414">
                  Stop Losing Jobs to{' '}
                  <span className="text-viridia" data-id="element-415">Slow Response Times.</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-416">
                  Whether it's a Yelp message at 10 PM, a missed call while
                  you're on a job, or a website inquiry — our AI systems respond
                  instantly, qualify the lead, and book the job 24/7.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-417">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-418">
                    Fix My Lead Response
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-419" />
                  </Link>
                  <a href="#see-it-in-action" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-420">
                    See It In Action
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Multiple Lead Sources */}
            <div className="relative h-[450px] lg:h-[550px] hidden md:block" data-id="element-421">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-id="element-422">
                {/* Yelp Message */}
                <motion.div initial={{
                opacity: 0,
                x: 50
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="bg-viridia-surface border border-red-900/30 p-4 rounded-2xl shadow-xl w-80 self-start ml-10 relative z-20" data-id="element-423">
                  <div className="flex items-center gap-3" data-id="element-424">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 flex-shrink-0" data-id="element-425">
                      <StarIcon className="w-5 h-5" data-id="element-426" />
                    </div>
                    <div className="flex-1" data-id="element-427">
                      <p className="text-white font-medium text-sm" data-id="element-428">
                        New Yelp Message
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-429">
                        "Do you do emergency repairs?"
                      </p>
                    </div>
                    <span className="text-xs text-gray-600" data-id="element-430">11:42 PM</span>
                  </div>
                </motion.div>

                {/* Missed Call */}
                <motion.div initial={{
                opacity: 0,
                x: -50
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.6
              }} className="bg-viridia-surface border border-gray-800 p-4 rounded-2xl shadow-xl w-80 self-end mr-10 relative z-20" data-id="element-431">
                  <div className="flex items-center gap-3" data-id="element-432">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 flex-shrink-0" data-id="element-433">
                      <PhoneIcon className="w-5 h-5" data-id="element-434" />
                    </div>
                    <div className="flex-1" data-id="element-435">
                      <p className="text-white font-medium text-sm" data-id="element-436">
                        Missed Call
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-437">From 602-555-0198</p>
                    </div>
                    <span className="text-xs text-gray-600" data-id="element-438">2:15 PM</span>
                  </div>
                </motion.div>

                {/* AI Response */}
                <motion.div initial={{
                opacity: 0,
                y: 50
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 1.2
              }} className="bg-viridia-surface border border-viridia/50 p-4 rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.15)] w-80 self-center relative mt-4 z-30" data-id="element-439">
                  <div className="absolute -top-3 -right-3 bg-viridia text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1" data-id="element-440">
                    <ZapIcon className="w-3 h-3" data-id="element-441" />
                    &lt; 30 SECONDS
                  </div>
                  <div className="flex items-center gap-3" data-id="element-442">
                    <div className="w-10 h-10 rounded-full bg-viridia/20 flex items-center justify-center text-viridia flex-shrink-0" data-id="element-443">
                      <BotIcon className="w-5 h-5" data-id="element-444" />
                    </div>
                    <div className="flex-1" data-id="element-445">
                      <p className="text-white font-medium text-sm" data-id="element-446">
                        AI Responded Instantly
                      </p>
                      <p className="text-gray-400 text-xs mt-1" data-id="element-447">
                        "Absolutely! Here's a link to book — pick the time that
                        works best for you."
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Connecting lines */}
                <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none" style={{
                filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.5))'
              }} data-id="element-448">
                  <motion.path d="M 120 100 C 120 150, 280 180, 280 250" fill="transparent" stroke="#10B981" strokeWidth="2" strokeDasharray="4 4" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 0.5
                }} transition={{
                  duration: 1,
                  delay: 0.8
                }} data-id="element-449" />
                  <motion.path d="M 280 200 C 280 250, 200 280, 200 350" fill="transparent" stroke="#10B981" strokeWidth="2" strokeDasharray="4 4" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 0.5
                }} transition={{
                  duration: 1,
                  delay: 1.4
                }} data-id="element-450" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE COST OF SLOW RESPONSE */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-451">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-452">
          <ScrollReveal data-id="element-453">
            <div className="text-center mb-16" data-id="element-454">
              <p className="text-sm font-bold text-red-400 tracking-widest uppercase mb-4" data-id="element-455">
                THE HARD TRUTH
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight" data-id="element-456">
                If You Don't Respond in 5 Minutes,
                <br data-id="element-457" />
                <span className="text-red-400" data-id="element-458">
                  You've Already Lost the Job.
                </span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-459">
            <div className="space-y-6" data-id="element-460">
              <ScrollReveal delay={0.1} data-id="element-461">
                <p className="text-lg text-gray-300 leading-relaxed" data-id="element-462">
                  A homeowner messages you on Yelp at 10 PM. You're asleep, so
                  you don't reply until 7 AM.{' '}
                  <span className="text-white font-semibold" data-id="element-463">
                    By then, they've already messaged 3 other businesses and
                    booked the first one that replied.
                  </span>
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.2} data-id="element-464">
                <p className="text-lg text-gray-300 leading-relaxed" data-id="element-465">
                  You're under a sink or on a roof. The phone rings and goes to
                  voicemail.{' '}
                  <span className="text-white font-semibold" data-id="element-466">
                    That lead doesn't leave a message — they just call the next
                    guy on Google.
                  </span>
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.3} data-id="element-467">
                <p className="text-lg text-gray-300 leading-relaxed" data-id="element-468">
                  Website form fills sit in an inbox for hours. Social DMs go
                  unseen. It's not a lead problem.{' '}
                  <span className="text-viridia font-bold" data-id="element-469">
                    It's a speed problem.
                  </span>
                </p>
              </ScrollReveal>
            </div>

            {/* Speed to Lead Comparison Visual */}
            <ScrollReveal delay={0.4} data-id="element-470">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 relative overflow-hidden" data-id="element-471">
                <h3 className="text-xl font-bold text-white mb-8 text-center" data-id="element-472">
                  The Speed to Lead Reality
                </h3>

                <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-700 before:to-transparent" data-id="element-473">
                  {/* Competitor (AI) */}
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-474">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-viridia-surface bg-viridia text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-475">
                      <ZapIcon className="w-4 h-4" data-id="element-476" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-viridia/30 bg-viridia/5 shadow-lg shadow-viridia/5" data-id="element-477">
                      <div className="flex items-center justify-between mb-1" data-id="element-478">
                        <span className="font-bold text-viridia" data-id="element-479">
                          Your Competitor (AI)
                        </span>
                        <span className="text-xs font-medium text-viridia bg-viridia/10 px-2 py-1 rounded" data-id="element-480">
                          30 Seconds
                        </span>
                      </div>
                      <p className="text-sm text-gray-300" data-id="element-481">
                        AI replies instantly, qualifies the lead, and books the
                        appointment.
                      </p>
                    </div>
                  </div>

                  {/* You (Manual) */}
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-482">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-viridia-surface bg-red-500 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-483">
                      <ClockIcon className="w-4 h-4" data-id="element-484" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-red-900/30 bg-red-500/5 shadow-lg" data-id="element-485">
                      <div className="flex items-center justify-between mb-1" data-id="element-486">
                        <span className="font-bold text-red-400" data-id="element-487">
                          You (Manual)
                        </span>
                        <span className="text-xs font-medium text-red-400 bg-red-500/10 px-2 py-1 rounded" data-id="element-488">
                          3 Hours Later
                        </span>
                      </div>
                      <p className="text-sm text-gray-400" data-id="element-489">
                        You finally check your phone and reply.{' '}
                        <span className="text-gray-300 italic" data-id="element-490">
                          "Sorry, already found someone."
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SEE IT IN ACTION SECTION */}
      <section id="see-it-in-action" className="py-24 bg-viridia-light" data-id="element-491">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-492">
          <ScrollReveal data-id="element-493">
            <div className="text-center mb-16" data-id="element-494">
              <p className="text-sm font-bold text-viridia tracking-widest uppercase mb-3" data-id="element-495">
                SEE IT IN ACTION
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-496">
                Instant Responses. Across Every Platform.
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-497">
                Our AI systems integrate directly with the platforms where your
                customers are already looking for you.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto" data-id="element-498">
            <ScrollReveal delay={0.1} data-id="element-499">
              <div className="bg-viridia-black rounded-2xl border border-gray-800 overflow-hidden shadow-2xl flex flex-col" data-id="element-500">
                {/* Header */}
                <div className="bg-gray-900 px-6 py-4 border-b border-gray-800 flex items-center justify-between" data-id="element-501">
                  <div className="flex items-center gap-3" data-id="element-502">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-503">
                      <StarIcon className="w-5 h-5 text-red-500" data-id="element-504" />
                    </div>
                    <div data-id="element-505">
                      <h3 className="text-white font-bold text-sm" data-id="element-506">
                        Nathan K.
                      </h3>
                      <p className="text-gray-400 text-xs" data-id="element-507">
                        Requested a quote • Entire interior painting
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-508">
                    <span className="relative flex h-3 w-3" data-id="element-509">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-viridia opacity-75" data-id="element-510"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-viridia" data-id="element-511"></span>
                    </span>
                    <span className="text-xs font-semibold text-viridia tracking-wider uppercase" data-id="element-512">
                      AI Active
                    </span>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="p-6 flex-grow flex flex-col gap-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-repeat h-[500px] overflow-y-auto" data-id="element-513">
                  {/* Message 1: Customer */}
                  <div className="flex gap-4" data-id="element-514">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-515">
                      <span className="text-gray-300 font-bold text-sm" data-id="element-516">
                        NK
                      </span>
                    </div>
                    <div className="bg-gray-800 rounded-2xl rounded-tl-none p-4 text-sm text-gray-200 max-w-[80%] shadow-md" data-id="element-517">
                      <p className="font-semibold text-white mb-1" data-id="element-518">
                        Request for Quote
                      </p>
                      <p data-id="element-519">Entire interior painting. 1000 - 1500 sq ft. ASAP.</p>
                      <span className="text-[10px] text-gray-500 mt-2 block" data-id="element-520">
                        1:22 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 2: AI */}
                  <div className="flex gap-4 flex-row-reverse" data-id="element-521">
                    <div className="w-10 h-10 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 shadow-lg shadow-viridia/20" data-id="element-522">
                      <BotIcon className="w-5 h-5 text-white" data-id="element-523" />
                    </div>
                    <div className="bg-viridia/20 border border-viridia/30 rounded-2xl rounded-tr-none p-4 text-sm text-white max-w-[80%] shadow-md" data-id="element-524">
                      Hi Nathan! We can definitely help with your interior
                      painting project. Are there specific colors you're
                      thinking about, or do you have a preferred paint brand?
                      <span className="text-[10px] text-viridia/60 mt-2 block text-right" data-id="element-525">
                        1:22 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 3: Customer */}
                  <div className="flex gap-4" data-id="element-526">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-527">
                      <span className="text-gray-300 font-bold text-sm" data-id="element-528">
                        NK
                      </span>
                    </div>
                    <div className="bg-gray-800 rounded-2xl rounded-tl-none p-4 text-sm text-gray-200 max-w-[80%] shadow-md" data-id="element-529">
                      Do you guys use Dunn-Edwards paint? And can I get a quote?
                      <span className="text-[10px] text-gray-500 mt-2 block" data-id="element-530">
                        1:24 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 4: AI */}
                  <div className="flex gap-4 flex-row-reverse" data-id="element-531">
                    <div className="w-10 h-10 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 shadow-lg shadow-viridia/20" data-id="element-532">
                      <BotIcon className="w-5 h-5 text-white" data-id="element-533" />
                    </div>
                    <div className="bg-viridia/20 border border-viridia/30 rounded-2xl rounded-tr-none p-4 text-sm text-white max-w-[80%] shadow-md" data-id="element-534">
                      Yes, we use Dunn-Edwards paint for our projects! To give
                      you an accurate quote for the 1000-1500 sq ft, we'd love
                      to do a quick walk-through. Want me to send over a link so
                      you can schedule a free estimate?
                      <span className="text-[10px] text-viridia/60 mt-2 block text-right" data-id="element-535">
                        1:24 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 5: Customer */}
                  <div className="flex gap-4" data-id="element-536">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-537">
                      <span className="text-gray-300 font-bold text-sm" data-id="element-538">
                        NK
                      </span>
                    </div>
                    <div className="bg-gray-800 rounded-2xl rounded-tl-none p-4 text-sm text-gray-200 max-w-[80%] shadow-md" data-id="element-539">
                      Perfect, yes please send the link.
                      <span className="text-[10px] text-gray-500 mt-2 block" data-id="element-540">
                        1:25 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 6: AI */}
                  <div className="flex gap-4 flex-row-reverse" data-id="element-541">
                    <div className="w-10 h-10 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 shadow-lg shadow-viridia/20" data-id="element-542">
                      <BotIcon className="w-5 h-5 text-white" data-id="element-543" />
                    </div>
                    <div className="bg-viridia/20 border border-viridia/30 rounded-2xl rounded-tr-none p-4 text-sm text-white max-w-[80%] shadow-md" data-id="element-544">
                      Here you go! You can pick a time that works best for you
                      here:
                      <br data-id="element-545" />
                      <br data-id="element-546" />
                      <span className="inline-flex items-center gap-1.5 bg-viridia/20 border border-viridia/30 rounded-md px-3 py-2 text-viridia text-xs font-bold cursor-pointer hover:bg-viridia/30 transition-colors" data-id="element-547">
                        📅 Schedule Free Estimate
                      </span>
                      <span className="text-[10px] text-viridia/60 mt-2 block text-right" data-id="element-548">
                        1:25 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 7: Customer */}
                  <div className="flex gap-4" data-id="element-549">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-550">
                      <span className="text-gray-300 font-bold text-sm" data-id="element-551">
                        NK
                      </span>
                    </div>
                    <div className="bg-gray-800 rounded-2xl rounded-tl-none p-4 text-sm text-gray-200 max-w-[80%] shadow-md" data-id="element-552">
                      Just scheduled for tomorrow morning. Thanks!
                      <span className="text-[10px] text-gray-500 mt-2 block" data-id="element-553">
                        1:28 PM
                      </span>
                    </div>
                  </div>

                  {/* Message 8: AI */}
                  <div className="flex gap-4 flex-row-reverse" data-id="element-554">
                    <div className="w-10 h-10 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 shadow-lg shadow-viridia/20" data-id="element-555">
                      <BotIcon className="w-5 h-5 text-white" data-id="element-556" />
                    </div>
                    <div className="bg-viridia/20 border border-viridia/30 rounded-2xl rounded-tr-none p-4 text-sm text-white max-w-[80%] shadow-md" data-id="element-557">
                      Great! We look forward to seeing you tomorrow, Nathan. Let
                      us know if you need anything else before then!
                      <span className="text-[10px] text-viridia/60 mt-2 block text-right" data-id="element-558">
                        1:28 PM
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer Input Mockup */}
                <div className="bg-gray-900 p-4 border-t border-gray-800" data-id="element-559">
                  <div className="bg-gray-800 rounded-full px-4 py-3 flex items-center justify-between" data-id="element-560">
                    <span className="text-gray-500 text-sm" data-id="element-561">
                      AI is handling this conversation...
                    </span>
                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center" data-id="element-562">
                      <ArrowRightIcon className="w-3 h-3 text-gray-400" data-id="element-563" />
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHAT WE ACTUALLY DO SECTION */}
      <section id="features" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-564">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-565">
          <ScrollReveal data-id="element-566">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-567">
              <p className="text-sm font-bold text-viridia tracking-widest uppercase mb-3" data-id="element-568">
                THE AUTOMATION ARSENAL
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-569">
                Tools built for speed and conversion.
              </h2>
              <p className="text-lg text-gray-400" data-id="element-570">
                You don't need to be a tech person. We build the systems so you
                can focus on your trade.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-571">
            {[{
            icon: StarIcon,
            title: 'Yelp AI Conversational Chatbot',
            desc: 'Instant AI responses to Yelp leads 24/7. Qualify prospects and book appointments while your competitors are sleeping.'
          }, {
            icon: PhoneIcon,
            title: 'Missed Call Text-Back',
            desc: 'Every missed call gets an instant text response. Turn a missed opportunity into a text conversation immediately.'
          }, {
            icon: MessageCircleIcon,
            title: 'Multi-Platform AI Chat',
            desc: 'Deploy our AI chatbot on your website, Facebook, and Instagram to capture leads from every traffic source.'
          }, {
            icon: MicIcon,
            title: 'AI Voice Receptionist',
            desc: "An AI-powered voice assistant that answers calls, takes messages, and routes inquiries when you can't pick up."
          }, {
            icon: CalendarIcon,
            title: 'Online Booking',
            desc: 'Let customers book directly from your website, Google listing, or text messages. Syncs to your calendar automatically.'
          }, {
            icon: InboxIcon,
            title: 'CRM & Unified Inbox',
            desc: 'All your messages — Yelp, texts, emails, Facebook DMs, Google messages — in one single dashboard.'
          }].map((feature, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-572">
                <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:border-viridia/50 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300 h-full group" data-id="element-573">
                  <div className="w-12 h-12 rounded-xl bg-gray-800 group-hover:bg-viridia/20 flex items-center justify-center text-gray-400 group-hover:text-viridia transition-colors duration-300 mb-6" data-id="element-574">
                    <feature.icon className="w-6 h-6" data-id="element-575" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-576">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-577">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-578">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-579">
          <ScrollReveal data-id="element-580">
            <div className="text-center mb-16" data-id="element-581">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-582">
                From New Lead to Booked Job in Under 60 Seconds
              </h2>
            </div>
          </ScrollReveal>

          <div className="relative" data-id="element-583">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-584"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-585">
              {[{
              step: '01',
              title: 'Lead Comes In',
              desc: 'A prospect calls, messages on Yelp, or fills out a website form.'
            }, {
              step: '02',
              title: 'AI Responds Instantly',
              desc: 'Within 30 seconds, our system sends a personalized response 24/7.'
            }, {
              step: '03',
              title: 'Lead Gets Qualified',
              desc: 'The AI asks the right questions, gathers details, and offers booking options.'
            }, {
              step: '04',
              title: 'Job Booked & Synced',
              desc: 'The appointment is confirmed, synced to your calendar, and you get notified.'
            }].map((item, index) => <ScrollReveal key={index} delay={index * 0.15} data-id="element-586">
                  <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative h-full" data-id="element-587">
                    <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-viridia text-white font-bold flex items-center justify-center shadow-lg" data-id="element-588">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 mt-2" data-id="element-589">
                      {item.title}
                    </h3>
                    <p className="text-gray-400" data-id="element-590">{item.desc}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-591">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-592"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-593">
          <ScrollReveal data-id="element-594">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-595">
              Stop Losing Leads to Slow Response Times
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-596">
              Let us build a system that responds instantly and books jobs while
              you sleep. Book a free workflow audit to see it in action.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-597">
              Fix My Business Workflow
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-598" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}